import { Component, computed, HostListener, inject, output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenubarModule } from 'primeng/menubar';
import { Popover, PopoverModule } from 'primeng/popover';
import { filter, Subject, takeUntil } from 'rxjs';
import { FlagPipe } from 'src/app/pipes/flags/flag-pipe';
import { TimeAgoPipe } from 'src/app/pipes/timeAgo/time-ago-pipe';
import { AccountService } from 'src/app/services/account-settings/account-service';
import { AuthService } from 'src/app/services/authentication/auth-service';
import { TranslationService } from 'src/app/services/configuration/translation-service';
import { GeneralService } from 'src/app/services/general/general-service';
import { JwtService } from 'src/app/services/jwt/jwt-service';
import { ModulesService } from 'src/app/services/modules/modules-service';
import { NotificationService } from 'src/app/services/notification/notification-service';
import { ThemeService } from 'src/app/services/theme/theme-service';
import { TimezoneFlag } from 'src/app/services/timezone-flag/timezone-flag';
import { Breadcrumbs } from 'src/app/shared/breadcrumbs/breadcrumbs';
import { FlagSelect } from 'src/app/shared/flag-select/flag-select';
import { SharedModule } from 'src/app/shared/shared/shared-module';

@Component({
  selector: 'app-nav-bar',
  imports: [SharedModule, RouterModule, Breadcrumbs, FlagPipe, FlagSelect, MenubarModule, PopoverModule, TimeAgoPipe],
  templateUrl: './nav-bar.html',
  styleUrls: ['./nav-bar.scss']
})
export class NavBar {
  private accountService = inject(AccountService);
  private authService = inject(AuthService);
  private tzService = inject(TimezoneFlag);
  private route = inject(ActivatedRoute);
  private generalService = inject(GeneralService);
  jwtService = inject(JwtService);
  router = inject(Router);

  private translationService = inject(TranslationService);
  private themeService = inject(ThemeService);
  private notificationService = inject(NotificationService);
  private modulesService = inject(ModulesService);

  @ViewChild('notif') notif!: Popover;
  @ViewChild('op') op!: Popover;

  readonly NavCollapsedMob = output();

  selectedCountries: any;
  selectedEntity: any;
  selectedLanguage: any;
  localTimeZone: any;
  countryTimeZone: any;
  accInfo: any;
  accDetails: any;
  navCollapsedMob: boolean = false;
  headerStyle: string = '';
  menuClass: boolean = false;
  collapseStyle: 'none' | 'block' = 'none';
  selectedTheme: number = 0;
  theme: any;
  languageList: any;
  loginInfo: any;
  searchValue: string = '';
  private destroy$ = new Subject<void>();
  countryList: any = 0;
  entityList: any;
  menus: any[] = [];
  notifications = this.notificationService.notificationList;
  unreadCount = this.notificationService.unreadCount;
  unreadNotifications = computed(() => this.notifications().filter((n) => !n.isRead));
  moduleList: any;
  // constructor() {
  //   effect(() => {
  //     console.log('notif', this.unreadNotifications());
  //   });
  // }

  ngOnInit() {
    this.tzService.localTime$.subscribe((info) => {
      this.localTimeZone = info;
    });

    this.tzService.countryTime$.subscribe((info) => {
      this.countryTimeZone = info;
    });

    this.GetLoginInfo();
    this.getModuleName();
  }

  //#region Popover
  toggleNotif(event: Event) {
    this.op?.hide();
    this.notif.toggle(event);
  }

  toggleProfile(event: Event) {
    this.notif?.hide();
    this.op.toggle(event);
  }

  @HostListener('window:scroll')
  onScroll() {
    this.op?.hide();
    this.notif?.hide();
  }

  //#region Login Info
  async GetLoginInfo() {
    this.loginInfo = this.jwtService.getClaims();
    this.selectedCountries = Number(this.loginInfo.scopeid);
    this.selectedEntity = Number(this.loginInfo.entityscopeid);
    this.selectedLanguage = Number(this.loginInfo.languageid);
    this.accInfo = this.authService.getUser();
    this.languageList = await this.authService.getLanguageList();
    this.setlanguage();
    this.countryDropdown();
    this.accGetByCode();
    this.getTheme();
    this.getHeaderEntityDropdown(this.selectedCountries);
    this.updateCountryAndEntity();
  }

  //#region Language
  async setlanguage() {
    this.translationService.setLocalStorage(this.selectedLanguage);
    await this.translationService.init(this.selectedLanguage);

    this.menus = [
      {
        label: this.translationService.translate('ACC_SETTINGS'),
        icon: 'icon profile-icon',
        command: () => {
          this.router.navigate(['/account-settings']);
          this.op.hide();
        }
      }
    ];
  }

  onLanguageChange() {
    this.authService.SwitchLanguage(this.selectedLanguage).subscribe({
      next: (results: any) => {
        this.switchCountry();
        this.setlanguage();
      }
    });
  }

  updateCountryAndEntity() {
    this.generalService.updateRequired$
      .pipe(
        filter((value) => value === true),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.countryDropdown();
        this.getHeaderEntityDropdown(this.selectedCountries);
        this.generalService.setNavUpdateRequired(false);
      });
  }

  //#region Account By Code
  accGetByCode() {
    this.accountService.AccountGetByCode(this.accInfo.userCode).subscribe({
      next: (result: any) => {
        this.accDetails = result.data;
        this.setMode(this.accDetails.modeId);
      }
    });
  }

  //#region Themes
  setTheme() {
    this.selectedTheme = Number(this.jwtService.getThemeId());
    const selected = this.theme?.find((t: any) => t.id === this.selectedTheme);
    if (selected) {
      this.themeService.applyTheme(selected);
    }
  }

  getTheme() {
    this.accountService.GetTheme().subscribe({
      next: (results: any) => {
        this.theme = results.data;
      },
      complete: () => {
        this.setTheme();
      }
    });
  }

  setMode(modeId: any) {
    this.themeService.clearMode();
    this.themeService.applyMode(modeId);
  }

  //#region Logout
  logout() {
    this.authService.Logout().subscribe({
      next: () => {
        this.authService.clearLogout();
      }
    });
  }

  //#region General
  toggleMobOption() {
    this.menuClass = !this.menuClass;
    this.headerStyle = this.menuClass ? 'none' : '';
    this.collapseStyle = this.menuClass ? 'block' : 'none';
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeMenu();
    }
  }

  closeMenu() {
    if (document.querySelector('app-navigation.pcoded-navbar')?.classList.contains('mob-open')) {
      document.querySelector('app-navigation.pcoded-navbar')?.classList.remove('mob-open');
    }
  }

  //#region Nav Bar
  get hideNavbar(): boolean {
    return this.router.url.startsWith('/sub-navigation');
  }

  //#region Country & Entity

  countryDropdown() {
    this.authService.GetHeaderCountry().subscribe({
      next: (results: any) => {
        this.countryList = results.data;
        const exists = this.countryList.some((e: any) => e.id === this.selectedCountries);
        if (!exists) {
          this.onCountryChange(this.countryList.length ? this.countryList[0].id : null);
        }
      }
    });
  }

  getHeaderEntityDropdown(id: any) {
    this.authService.GetHeaderEntity(id).subscribe({
      next: (results: any) => {
        this.entityList = results.data;
        const exists = this.entityList.some((e: any) => e.id === this.selectedEntity);
        if (!exists) {
          this.selectedEntity = this.entityList.length ? this.entityList[0].id : null;
          if (this.selectedEntity === null) {
            this.countryDropdown();
          } else {
            this.switchCountry();
          }
        }
      }
    });
  }

  onCountryChange(countryScopeId: any) {
    this.selectedCountries = countryScopeId;
    this.selectedEntity = '';
    this.switchCountry();
  }

  onEntityChange(entityScopeId: any) {
    this.selectedEntity = entityScopeId;
    this.switchCountry();
  }

  switchCountry() {
    const formData = new FormData();
    formData.append('countryScopeId', this.selectedCountries);
    if (this.selectedEntity !== null) {
      formData.append('entityScopeId', this.selectedEntity);
    }
    this.authService.SwitchCountry(formData).subscribe({
      next: (results: any) => {
        const data = results.data;
        this.jwtService.saveToken(data.token);
        window.location.reload();
        localStorage.setItem('banner', data.bannerUrl);
      },
      error: () => {
        this.entityList = [];
        this.selectedEntity = null;
      }
    });
  }

  //#region Notifications
  clikNotif(id: any) {
    const latest = this.notificationService.getNotificationById(id);
    if (!latest || latest.notificationType !== 'REQUEST') return;
    this.notificationService.markAsRead([id]);
    if (latest.url && latest.title === 'IMPORT') {
      window.open(latest.url, '_blank');
      return;
    }
    this.router.navigate(['/', latest.url, 'details', latest.notificationRefId]);
  }

  goToNotification() {
    this.router.navigate(['/notifications'], { relativeTo: this.route });
    this.notif.hide();
  }

  getModuleName() {
    this.generalService.GetGeneralModuleList().subscribe({
      next: (results: any) => {
        this.moduleList = results.data;
      }
    });
  }

  getRefType(n: any) {
    return this.moduleList?.find((x: any) => x.code === n)?.description ?? '';
  }

  stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    div.querySelectorAll('p').forEach((p) => {
      p.appendChild(document.createTextNode('\n'));
    });

    return (div.textContent || '').replace(/\n\s*\n/g, '\n').trim();
  }

  //#region Elastic/Global Search
  onGlobalSearch() {
    if (!this.searchValue?.trim()) return;

    this.modulesService.setGlobalSearchValue(this.searchValue);
    if (this.router.url !== '/search') {
      this.router.navigate(['/search']);
    }
  }

  clearGlobalSearch() {
    this.searchValue = '';
    this.modulesService.closeGlobalSearch();
    // this.onGlobalSearch();
  }

  onSearchChange() {
    if (this.searchValue === '') this.modulesService.closeGlobalSearch();
    // this.onGlobalSearch();
  }
  //#endregion
}
